import { resendInvitationProvider } from '@/apis/Providers/requests.ts'
import { LogUserActivity } from '@/app/apis/users/requests'
import { Button } from '@/app/common/components/button/Button'
import { LOG_TYPES } from '@/app/common/constants'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/common/components/Card.tsx'
import MultipleSelector from '@/common/components/MultipleSelector.tsx'
import Pagination from '@/common/components/Pagination.tsx'
import SearchInput from '@/common/components/SearchInput.tsx'
import ApplicationStatusBadge from '@/common/components/StatusBadge'
import { CircleArrowsIcon } from '@/common/icons/arrows.tsx'
import { FilterIcon } from '@/common/icons/general.tsx'
import { cn } from '@/common/utilities.ts'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { formatDateToUsersTimezone } from '@/lib/utils'
import ProviderImportModal from '@/pages/providers/bulk_upload_providers/View.tsx'
import InviteProvider from '@/pages/providers/Invite/InviteProvider.tsx'
import { ProviderQueries } from '@/queries/providers/queries'
import { ProviderList } from '@/queries/providers/requests'
import { AppRoutePaths } from '@/routing/route_paths'
import { Provider } from '@/schemas/provider.schema'
import { RootState } from '@/storage/store'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useDebounce } from '@uidotdev/usehooks'
import { format } from 'date-fns'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import AdminAuthGuard from './admin-auth-guard'

type ListItem = {
  id: string | number
  name: string
}

const TABLE_FILTERS_STATUS: ListItem[] = [
  { id: 1, name: 'Invitation Sent' },
  { id: 2, name: 'Application In Progress' },
  { id: 3, name: 'Application Submitted' },
  { id: 4, name: 'Application Accepted' },
  { id: 5, name: 'Application Rejected' },
]

const columnHelper = createColumnHelper<Provider>()
const RECORDS_PER_PAGE = 10 as const
const THREE_MINUTES = 1000 * 60 * 3
const InitialProviders: ProviderList = []

export default function ProvidersPage() {
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setsearch] = useState('')
  const [inviteModalVisible, setInviteModalVisible] = useState(false)
  const [importModalVisible, setImportModalVisible] = useState(false)
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)
  const [selectedFilter, setSelectedFilter] = useState<ListItem[]>([])

  const { fireAlert } = useAppAlert()

  const navigate = useNavigate()
  const { selectedProgramYear } = useSelector((s: RootState) => s.programs)
  const debouncedTerm = useDebounce(search, 500)

  const statusInFilter = selectedFilter.map((f) => f.id).join(',')

  const ProviderListQuery = useQuery(
    ProviderQueries.getPaginatedProviders(
      selectedProgramYear?.id ?? 0,
      recordsPerPage,
      currentPage,
      debouncedTerm,
      undefined,
      statusInFilter,
      {
        enabled: true,
        placeholderData: keepPreviousData,
        refetchInterval: THREE_MINUTES,
      }
    )
  )

  useEffect(() => {
    setCurrentPage(1)
  }, [search])

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Provider page',
      })
      hasLoggedPageView.current = true
    }
  }, [])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
  }

  const handleResendInvitation = async (id: number) => {
    const payload = {
      provider_id: id,
      program_id: selectedProgramYear?.id ?? 0,
    }

    const globalOnId = 1
    const response = await resendInvitationProvider(payload, globalOnId)

    if (response.error) {
      const errorMessage =
        response.error.message ||
        'An unexpected error occurred. Please try again later.'

      await fireAlert({
        variant: 'error',
        message: errorMessage,
      })
      return
    }

    await fireAlert({
      variant: 'success',
      message: response.data?.message || 'Invitation resent successfully!',
    })

    refetchProviderList()
  }

  const columns = [
    columnHelper.accessor('id', {
      id: 'id',
      header: () => <span className="pl-4">Id</span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center">
            <div className="w-3/4 sm:w-full font-bold pl-4">
              <div className="font-medium text-gray-900 flex justify-between items-center">
                <span>{row.id}</span>
              </div>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('name', {
      id: 'name',
      header: () => <span className="pl-4">Name</span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center">
            <div className="w-3/4 sm:w-full font-bold pl-4">
              <div className="font-medium text-gray-900 flex justify-between items-center">
                <span>{row.name}</span>
              </div>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('provider_application.provider_categories', {
      id: 'category',
      header: () => <span>Category</span>,
      cell: (info) => {
        const row = info.row.original
        const categories = row.provider_application?.provider_categories
          ?.map((category) => category.global_provider_category?.name)
          .filter(Boolean)
          .join(', ')
        return <>{categories}</>
      },
    }),
    columnHelper.accessor('state_name', {
      id: 'state_name',
      header: () => <span>State</span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center">
            <div className="font-medium text-gray-900 flex justify-between items-center">
              <span>{row.state_name}</span>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('provider_application.status', {
      id: 'status',
      header: () => (
        <span className="pl-4 inline-flex items-center gap-1 w-44 whitespace-nowrap">
          Status
        </span>
      ),
      cell: (info) => {
        const row = info.row.original
        const status = row.provider_application?.status ?? ''
        return <ApplicationStatusBadge status={status} />
      },
    }),
    columnHelper.accessor('id', {
      header: () => (
        <span className="pl-4 inline-flex items-center gap-1 w-44 whitespace-nowrap">
          Submitted At
        </span>
      ),
      cell: (info) => {
        const row = info.row.original
        const submitted_at = row.provider_application?.submitted_at ?? ''
        return (
          <span>
            {submitted_at ? formatDateToUsersTimezone(submitted_at) : ''}
          </span>
        )
      },
    }),
    columnHelper.display({
      id: 'action',
      header: () => (
        <span className="inline-block w-20 whitespace-nowrap text-right pr-2">
          Action
        </span>
      ),
      cell: (info) => {
        const row = info.row.original
        const status = row.provider_application?.status
        return (
          <>
            {status === 'Invited' ? (
              <Button
                className="w-[140px] text-sm font-medium truncate"
                onClick={() => {
                  handleResendInvitation(row.id)
                }}
              >
                Resend Invitation
              </Button>
            ) : (
              <Button
                size="xs"
                className="w-[140px] text-sm font-medium truncate"
                onClick={() => {
                  navigate(
                    AppRoutePaths.providers.provider_details.provider_info.replace(
                      ':providerId',
                      row.id.toString()
                    )
                  )
                }}
              >
                Review
              </Button>
            )}
          </>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: ProviderListQuery.data?.data ?? InitialProviders,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: ProviderListQuery.data?.last_page,
  })

  const refetchProviderList = () =>
    ProviderListQuery.refetch({ throwOnError: true, cancelRefetch: false })

  return (
    <>
      <div className="flex ml-auto gap-3">
        <AdminAuthGuard requiredPermissions={['provider_management_create']}>
          <Button
            variant="outline"
            size="lg"
            onClick={() => {
              setImportModalVisible((prev) => !prev)
            }}
          >
            Upload Providers
          </Button>

          <Button
            size="lg"
            onClick={() => {
              setInviteModalVisible((prev) => !prev)
            }}
          >
            Invite Provider
          </Button>
        </AdminAuthGuard>
      </div>
      <section aria-labelledby="providers-list-title">
        <Card className="mt-6">
          <CardHeader className="flex flex-col sm:flex-row sm:justify-between sm:items-center text-md font-semibold gap-4 sm:gap-0">
            <div className="text-left text-lg md:text-md text-gray-900 w-full">
              Providers
              <span className="hidden mt-1 w-15ch text-sm font-normal text-gray-400 lg-sm:block lg-sm:w-full">
                {ProviderListQuery.isFetching
                  ? 'refreshing data...'
                  : 'last updated at: ' +
                    format(ProviderListQuery.dataUpdatedAt, 'hh:mm:ss a')}{' '}
              </span>
            </div>
            <div className="w-full sm:w-auto">
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 w-full gap-3">
                <MultipleSelector
                  className="w-full sm:w-[220px] lg:w-[250px]"
                  list={TABLE_FILTERS_STATUS.sort((a, b) =>
                    a.name.localeCompare(b.name)
                  )}
                  selected={selectedFilter}
                  setSelected={setSelectedFilter}
                  icon={<FilterIcon className="text-primary" />}
                />
                <SearchInput
                  className="w-full sm:w-[200px] lg:w-[250px]"
                  id="table_search"
                  placeholder="Search Providers"
                  value={search}
                  onChange={(e) => setsearch(e.target.value)}
                />
                <div className="flex space-x-2 lg-sm:flex-col lg-sm:space-x-0">
                  <Button
                    variant="outline"
                    className="max-w-12"
                    size="md"
                    disabled={ProviderListQuery.isFetching}
                    onClick={refetchProviderList}
                  >
                    <CircleArrowsIcon className="text-base" />
                  </Button>
                  <span className="block w-15ch text-sm font-normal text-gray-400 lg-sm:w-full lg-sm:hidden">
                    {ProviderListQuery.isFetching
                      ? 'refreshing data...'
                      : 'last updated at: ' +
                        format(ProviderListQuery.dataUpdatedAt, 'hh:mm:ss a')}
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="w-full p-0">
            <div className="overflow-auto w-full h-[55dvh]">
              <table className="w-full table-auto">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={header.id}
                          className={cn(
                            'px-2 py-3.5 text-left text-sm font-semibold text-gray-900 whitespace-nowrap',
                            'min-w-[140px] max-w-[220px]'
                          )}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          className={cn(
                            'py-4 px-2 text-sm text-left text-gray-500 border-t border-gray-200',
                            'min-w-[140px] max-w-[220px] truncate whitespace-nowrap'
                          )}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
          <CardFooter className="p-0 border-t border-transparent mt-5">
            <Pagination
              itemsPerPage={recordsPerPage}
              currentPage={currentPage}
              totalRows={ProviderListQuery.data?.total ?? 0}
              onPageChange={handlePageChange}
              onPerPageChange={handlePerPageChange}
            />
          </CardFooter>

          <InviteProvider
            inviteModalVisible={inviteModalVisible}
            setInviteModalVisible={setInviteModalVisible}
            selectedProgramYear={selectedProgramYear?.id ?? 0}
            inviteIsFetching={false}
          />

          <ProviderImportModal
            importModalVisible={importModalVisible}
            setImportModalVisible={setImportModalVisible}
          />
        </Card>
      </section>
    </>
  )
}
